/* Leave / Half-Day Application — Salesperson section */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import { CalendarDays, Clock, Send, Users } from "lucide-react";
import DateInput from "../../components/DateInput";
import Paginated from "../../components/Paginated";

const todayStr = () => new Date().toISOString().slice(0, 10);

const STATUS_BADGE = {
  pending: "bg-amber-50 text-amber-700",
  approved: "bg-green-50 text-green-700",
  rejected: "bg-red-50 text-red-700",
  reverted: "bg-slate-100 text-slate-600",
};

export default function LeaveApplication() {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  const [mine, setMine] = useState([]);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({
    leave_type: "leave",           // holiday/leave OR half day — must be selected first
    from_date: "", to_date: "",
    half_day_date: "",
    designation: "", department: "",
    reason: "",
    works_in_hand: "",
    handover_store: user?.store || "",
    handover_to: "",
    contact_1: user?.phone || "", contact_2: "",
  });

  const load = () => {
    api.get("/api/crm/leave-applications/mine").then((r) => setMine(r.data || []));
    api.get("/api/crm/leave-applications/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => {
    api.get("/api/crm/stores").then((r) => setStores(r.data || [])).catch(() => {});
    load();
  }, []);

  // Salespeople of the selected store (excluding self) — the handover list
  useEffect(() => {
    if (!form.handover_store) { setSalespeople([]); return; }
    api.get(`/api/crm/salespeople-directory?store=${encodeURIComponent(form.handover_store)}`)
      .then((r) => setSalespeople((r.data || []).filter((s) => s.id !== user?.id)))
      .catch(() => setSalespeople([]));
    // eslint-disable-next-line
  }, [form.handover_store, user?.id]);

  // Days are calculated automatically from the date pickers
  const days = useMemo(() => {
    if (form.leave_type === "half_day") return form.half_day_date ? 0.5 : 0;
    if (!form.from_date || !form.to_date) return 0;
    const d = (new Date(form.to_date) - new Date(form.from_date)) / 86400000;
    return d >= 0 ? d + 1 : 0;
  }, [form.leave_type, form.from_date, form.to_date, form.half_day_date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.handover_to) { toast.error("Select the salesperson you are handing your tasks to"); return; }
    if (!form.contact_1.trim()) { toast.error("One contact number is mandatory"); return; }
    try {
      await api.post("/api/crm/leave-applications", form);
      toast.success("Application submitted for approval");
      setForm({
        leave_type: "leave", from_date: "", to_date: "", half_day_date: "",
        designation: "", department: "", reason: "", works_in_hand: "",
        handover_store: user?.store || "", handover_to: "",
        contact_1: user?.phone || "", contact_2: "",
      });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to submit"); }
  };

  const isHalf = form.leave_type === "half_day";
  const inputCls = "w-full border border-line rounded-lg px-3 py-2 bg-pearl text-sm";

  return (
    <div className="p-6 lg:p-8 grid lg:grid-cols-2 gap-6">
      {/* ============ The Leave Application Form ============ */}
      <div>
        <div className="eyebrow">Time Off</div>
        <h1 className="font-display text-3xl text-maroon">Leave Application</h1>
        <p className="text-xs text-ash mt-1">
          Leaves must be applied at least <b>2 days</b> in advance · half days at least <b>1 day</b> in advance.
          You may take the leave only after an administrator approves it.
        </p>

        <form onSubmit={submit} className="bg-white border border-line rounded-xl p-6 mt-4 space-y-4">
          {/* Auto-fetched: name + application date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="eyebrow text-[10px]">Name (auto)</div>
              <div className="border border-line rounded-lg px-3 py-2 bg-pearl/60 text-sm text-ash" data-testid="leave-name">{user?.name}</div>
            </div>
            <div>
              <div className="eyebrow text-[10px]">Applying on (auto)</div>
              <div className="border border-line rounded-lg px-3 py-2 bg-pearl/60 text-sm text-ash" data-testid="leave-applied-on">{todayStr()}</div>
            </div>
          </div>

          {/* First: is it a holiday/leave or a half day? */}
          <div>
            <div className="eyebrow text-[10px] mb-2">Type — select first</div>
            <div className="flex gap-2">
              {[["leave", "Holiday / Leave", CalendarDays], ["half_day", "Half Day", Clock]].map(([v, l, Icon]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, leave_type: v })}
                  className={`flex-1 flex items-center justify-center gap-2 border rounded-full px-4 py-2.5 text-sm transition-all ${form.leave_type === v ? "bg-maroon text-pearl border-maroon" : "border-line hover:border-maroon"}`}
                  data-testid={`leave-type-${v}`}>
                  <Icon size={15}/> {l}
                </button>
              ))}
            </div>
          </div>

          {/* Dates */}
          {isHalf ? (
            <label className="block text-sm">
              <span className="eyebrow text-[10px]">Half day on which date? (min. 1 day prior)</span>
              <div className="mt-1"><DateInput yearsBack={0} yearsAhead={1} value={form.half_day_date} onChange={(v) => setForm({ ...form, half_day_date: v })} data-testid="leave-halfday-date"/></div>
            </label>
          ) : (
            <div className="grid grid-cols-2 gap-3 items-end">
              <label className="block text-sm">
                <span className="eyebrow text-[10px] block min-h-[28px]">Leave from (min. 2 days prior)</span>
                <div className="mt-1"><DateInput yearsBack={0} yearsAhead={1} value={form.from_date} onChange={(v) => setForm({ ...form, from_date: v, to_date: form.to_date || v })} data-testid="leave-from"/></div>
              </label>
              <label className="block text-sm">
                <span className="eyebrow text-[10px] block min-h-[28px]">Leave to</span>
                <div className="mt-1"><DateInput yearsBack={0} yearsAhead={1} value={form.to_date} onChange={(v) => setForm({ ...form, to_date: v })} data-testid="leave-to"/></div>
              </label>
            </div>
          )}

          {/* Auto-calculated days */}
          <div className="flex items-center justify-between bg-gold/10 border border-gold/40 rounded-lg px-4 py-2.5 text-sm">
            <span className="text-ash">Duration (calculated automatically)</span>
            <b className="font-display text-lg text-maroon" data-testid="leave-days">{days || "—"} {days === 0.5 ? "day (half)" : days === 1 ? "day" : "days"}</b>
          </div>

          {/* Designation + Department (inputs) */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm"><span className="eyebrow text-[10px]">Designation</span>
              <input required placeholder="e.g. Senior Salesperson" className={`${inputCls} mt-1`} value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="leave-designation"/></label>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Department</span>
              <input required placeholder="e.g. Gold Counter" className={`${inputCls} mt-1`} value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="leave-department"/></label>
          </div>

          {/* Reason */}
          <label className="block text-sm"><span className="eyebrow text-[10px]">Reason for the {isHalf ? "half day" : "leave"}</span>
            <textarea required rows={2} placeholder={isHalf ? "Why do you need this half day?" : "Why do you need this leave?"} className={`${inputCls} mt-1`} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="leave-reason"/></label>

          {/* Works in hand + handover */}
          <div className="border-t border-line pt-4">
            <div className="eyebrow text-[10px] mb-2 flex items-center gap-1"><Users size={12}/> Task Handover</div>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Works currently in your hand</span>
              <textarea required rows={2} placeholder="List the works that were assigned to you…" className={`${inputCls} mt-1`} value={form.works_in_hand} onChange={(e) => setForm({ ...form, works_in_hand: e.target.value })} data-testid="leave-works"/></label>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <label className="block text-sm"><span className="eyebrow text-[10px]">Store</span>
                <select required className={`${inputCls} mt-1`} value={form.handover_store} onChange={(e) => setForm({ ...form, handover_store: e.target.value, handover_to: "" })} data-testid="leave-handover-store">
                  <option value="">Select store…</option>
                  {stores.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select></label>
              <label className="block text-sm"><span className="eyebrow text-[10px]">Hand over tasks to</span>
                <select required className={`${inputCls} mt-1`} value={form.handover_to} onChange={(e) => setForm({ ...form, handover_to: e.target.value })} data-testid="leave-handover-to">
                  <option value="">Select salesperson…</option>
                  {salespeople.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select></label>
            </div>
            {form.handover_store && salespeople.length === 0 && (
              <div className="text-[11px] text-red-600 mt-1">No other salesperson found in this store — pick another store.</div>
            )}
          </div>

          {/* Contact numbers */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm"><span className="eyebrow text-[10px]">Contact number (mandatory)</span>
              <input required maxLength={10} inputMode="numeric" placeholder="10-digit mobile" className={`${inputCls} mt-1`} value={form.contact_1} onChange={(e) => setForm({ ...form, contact_1: e.target.value.replace(/\D/g, "").slice(0, 10) })} data-testid="leave-contact1"/></label>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Second contact (optional)</span>
              <input maxLength={10} inputMode="numeric" placeholder="Alternate number" className={`${inputCls} mt-1`} value={form.contact_2} onChange={(e) => setForm({ ...form, contact_2: e.target.value.replace(/\D/g, "").slice(0, 10) })} data-testid="leave-contact2"/></label>
          </div>

          <button className="btn-maroon w-full justify-center" data-testid="leave-submit"><Send size={14}/> Submit for Approval</button>
        </form>
      </div>

      {/* ============ My applications + counters ============ */}
      <div>
        <div className="eyebrow mb-3">My leave statistics</div>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            ["Leave days (year)", summary?.leave_days_taken_year ?? 0],
            ["Half days (year)", summary?.half_days_taken_year ?? 0],
            ["Pending", mine.filter((m) => m.status === "pending").length],
          ].map(([l, v]) => (
            <div key={l} className="bg-white border border-line rounded-xl p-3 text-center">
              <div className="font-display text-2xl text-maroon">{v}</div>
              <div className="text-[9px] uppercase tracking-widest text-ash">{l}</div>
            </div>
          ))}
        </div>

        <div className="eyebrow mb-3">My applications</div>
        <Paginated items={mine} pageSize={6}>
          {(pageItems) => (
            <div className="space-y-2">
              {pageItems.map((a) => (
                <div key={a.id} className="bg-white border border-line rounded-xl p-4 text-sm" data-testid={`leave-app-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <b className="text-maroon">{a.leave_type === "half_day" ? `Half day · ${a.half_day_date}` : `Leave · ${a.from_date} → ${a.to_date}`} ({a.days}d)</b>
                    <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_BADGE[a.status]}`}>{a.status}</span>
                  </div>
                  <div className="text-xs text-ash mt-1">Reason: {a.reason}</div>
                  <div className="text-xs text-ash">Handover to: {a.handover_to_name} · Back on {a.returning_on}</div>
                  {a.status === "reverted" && (
                    <div className="text-[11px] mt-1 text-slate-600">Marked as <b>not taken</b> by {a.reverted_by_name} (super admin) — this no longer counts in your leave totals.</div>
                  )}
                  {a.status !== "pending" && a.status !== "reverted" && (
                    <div className="text-[11px] mt-1 text-ash">
                      {a.status === "approved" ? "Approved" : "Rejected"} by <b>{a.decided_by_name}</b> ({(a.decided_by_role || "").replace("_", " ")})
                      {a.decision_note && <> — “{a.decision_note}”</>}
                    </div>
                  )}
                  {a.status === "pending" && <div className="text-[11px] mt-1 text-amber-700">Awaiting approval — you can take the {a.leave_type === "half_day" ? "half day" : "leave"} only after it is granted.</div>}
                </div>
              ))}
            </div>
          )}
        </Paginated>
        {mine.length === 0 && <div className="text-center text-xs text-ash py-10 bg-white border border-line rounded-xl">No applications yet.</div>}
      </div>
    </div>
  );
}
